import { InsufficientCreditsDialog } from "@/components/DialogBox/InsufficientCreditDialog";
import { ProgressDrawer } from "@/components/ProgressDrawer/ProgressDrawer";
import { ProgressRow } from "@/components/ProgressDrawer/ProgressRow";
import {
  CaseUpload,
  compareUpload,
  handleCancelUpload,
  handleCancelUploads,
  retryUpload,
  selectHasUnpaidUploads,
  selectIsUploading,
  UploadStatus
} from "@/store/slices/caseUploadsSlice";
import { RootState, store } from "@/store/store";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

const ProgressRowContainer = ({ id }: { id: number }) => {
  const name = useSelector((state: RootState) => state.uploads[id].name);
  const error = useSelector((state: RootState) => state.uploads[id].error);
  const complete = useSelector((state: RootState) => state.uploads[id].status === UploadStatus.Complete);
  const pending = useSelector((state: RootState) => state.uploads[id].status === UploadStatus.Pending);
  const cancelled = useSelector((state: RootState) => state.uploads[id].cancelled);

  return (
    <ProgressRow
      name={name}
      error={error}
      cancelled={cancelled}
      complete={complete}
      handleOnClickCancel={() => store.dispatch(handleCancelUpload(id))}
      handleOnClickRetry={() => store.dispatch(retryUpload(id))}
      progressSelector={(state: RootState) => (pending ? undefined : state.uploads[id].progress)}
    />
  );
};

const InsufficientCredits = () => {
  const [insufficientCreditsDialogOpen, setInsufficientCreditsDialogOpen] = useState<boolean>(false);
  const hasUnpaidUploads = useSelector(selectHasUnpaidUploads);

  useEffect(() => {
    if (hasUnpaidUploads) {
      setInsufficientCreditsDialogOpen(true);
    }
  }, [hasUnpaidUploads]);

  return <InsufficientCreditsDialog open={insufficientCreditsDialogOpen} setOpen={setInsufficientCreditsDialogOpen} showExtendedMessage />;
};

export const UploadProgressDrawer = () => {
  const hasInProgress = useSelector(selectIsUploading);

  const selectUploads = useMemo(() => {
    return createSelector(
      [(state: RootState) => state.uploads],
      (content) =>
        Object.values(content)
          .filter((caseData) => caseData != null)
          .sort((a, b) => compareUpload(a as CaseUpload, b as CaseUpload))
          .map((upload) => (upload as CaseUpload).uploadId),
      {
        memoizeOptions: {
          resultEqualityCheck: shallowEqual
        }
      }
    );
  }, []);
  const uploads = useSelector((state: RootState) => selectUploads(state));

  return (
    <>
      <ProgressDrawer
        handleCancelAll={() => store.dispatch(handleCancelUploads())}
        shouldOnCloseShowModal={() => {
          return hasInProgress;
        }}
        hasUploads={uploads.length > 0}
      >
        {uploads.map((id) => (
          <ProgressRowContainer key={id} id={id} />
        ))}
      </ProgressDrawer>
      <InsufficientCredits />
    </>
  );
};
